import Close from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import useEntryWorkspaceMutations from 'api/operation/entry/useEntryWorkspaceMutations';
import ShelfAndPartMap from 'components/shared/WarehouseGeoGraphMap/ShelfAndPartMap/ShelfAndPartMap';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';

const PICK_STEPS = {
  SECTION: 'section',
  AREA: 'area',
  SHELF: 'shelf',
};

const stepTitles = {
  [PICK_STEPS.SECTION]: 'لطفا بخش مورد نظر را انتخاب کنید',
  [PICK_STEPS.AREA]: 'لطفا ناحیه مورد نظر را انتخاب کنید',
  [PICK_STEPS.SHELF]: 'قفسه و بخش را انتخاب کنید',
};

const toShelfAndPartMapData = shelves => {
  if (!Array.isArray(shelves)) return [];

  return shelves.map(shelf => ({
    guid: shelf.guid,
    display: shelf.display,
    parts: (shelf.parts || [])
      .filter(part => part.isVisible !== false)
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(part => ({
        guid: part.guid,
        display: part.display,
        isFull: Boolean(part.isFull),
        status: part.status,
        cellCount: Number(part.cellCount) || 0,
        emptyCellCount: Number(part.emptyCellCount) || 0,
        productCellCount: Number(part.productCellCount) || 0,
        cells: part.cells || [],
      })),
  }));
};

const LocationPickerDialog = ({
  open,
  onClose,
  direction,
  product,
  prepareOrigin,
  prepareDestination,
  loading,
  defaultCount,
}) => {
  const [{ operationSettings }] = useCookies(['operationSettings']);
  const zoneGuid = operationSettings?.defaultZone?.key;
  const { enqueueSnackbar } = useSnackbar();
  const { getPlacementSections, getPlacementAreas, getShelfAndPart, getCells } =
    useEntryWorkspaceMutations();

  const [step, setStep] = useState(PICK_STEPS.SECTION);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [cellsLoading, setCellsLoading] = useState(false);
  const [isViewerModalOpen, setIsViewerModalOpen] = useState(false);
  const [sections, setSections] = useState([]);
  const [areas, setAreas] = useState([]);
  const [shelves, setShelves] = useState([]);
  const [cells, setCells] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedPart, setSelectedPart] = useState(null);
  const [count, setCount] = useState(String(defaultCount || 1));

  const resetState = useCallback(() => {
    setStep(PICK_STEPS.SECTION);
    setFetchLoading(false);
    setCellsLoading(false);
    setIsViewerModalOpen(false);
    setSections([]);
    setAreas([]);
    setShelves([]);
    setCells([]);
    setSelectedSection(null);
    setSelectedArea(null);
    setSelectedPart(null);
    setCount(String(defaultCount || 1));
  }, [defaultCount]);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    if (!zoneGuid) {
      enqueueSnackbar('لطفا ابتدا منطقه خود را در تنظیمات انتخاب کنید', {
        variant: 'warning',
      });
      return;
    }

    let cancelled = false;

    const loadSections = async () => {
      setFetchLoading(true);
      const result = await getPlacementSections(zoneGuid);
      if (cancelled) return;
      setSections(Array.isArray(result) ? result : result?.list ?? []);
      setFetchLoading(false);
      setStep(PICK_STEPS.SECTION);
    };

    loadSections();

    return () => {
      cancelled = true;
    };
  }, [open, zoneGuid, enqueueSnackbar, getPlacementSections, resetState]);

  const handleSelectSection = async section => {
    setSelectedSection(section);
    setFetchLoading(true);
    const result = await getPlacementAreas(section.guid);
    setAreas(Array.isArray(result) ? result : result?.list ?? []);
    setFetchLoading(false);
    setStep(PICK_STEPS.AREA);
  };

  const handleSelectArea = async area => {
    setSelectedArea(area);
    setFetchLoading(true);
    const result = await getShelfAndPart(area.guid);
    setShelves(Array.isArray(result) ? result : result?.list ?? []);
    setFetchLoading(false);
    setStep(PICK_STEPS.SHELF);
  };

  const handleClickPart = async partGuid => {
    const part = shelves
      .flatMap(shelf => shelf.parts || [])
      .find(item => item.guid === partGuid);

    if (!part) return;

    setSelectedPart(part);
    setIsViewerModalOpen(true);
    setCellsLoading(true);
    const result = await getCells(part.guid);
    setCells(Array.isArray(result) ? result : result?.list ?? []);
    setCellsLoading(false);
  };

  const submitCell = async cellItem => {
    const cell = cellItem?.cell || cellItem;
    if (!cell?.guid) return;

    if (direction === 'origin') {
      if (!cell.display?.length) {
        enqueueSnackbar('سلول خالی می باشد', { variant: 'warning' });
        return;
      }

      const parsedCount = Number.parseInt(count, 10);
      if (!Number.isFinite(parsedCount) || parsedCount <= 0) {
        enqueueSnackbar('تعداد معتبر نیست', { variant: 'warning' });
        return;
      }

      const ok = await prepareOrigin({
        cellGuid: cell.guid,
        count: String(parsedCount),
      });
      if (ok) onClose();
      return;
    }

    if (cell.display?.length && cell.packGuid !== product?.productPackGuid) {
      enqueueSnackbar('سلول پر می باشد', { variant: 'warning' });
      return;
    }

    const ok = await prepareDestination(cell.guid);
    if (ok) onClose();
  };

  const shelfMapData = useMemo(() => toShelfAndPartMapData(shelves), [shelves]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={step === PICK_STEPS.SHELF ? 'lg' : 'sm'}
      sx={{ direction: 'rtl' }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {stepTitles[step]}
        <IconButton size="small" onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {product ? (
          <Typography variant="body2" color="text.secondary" mb={2}>
            {product.title} - {product.packName} - x{product.quantity ?? 0}
          </Typography>
        ) : null}

        {fetchLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!fetchLoading && step === PICK_STEPS.SECTION ? (
          <Stack gap={1}>
            {sections.map(section => (
              <Button
                key={section.guid}
                variant="contained"
                onClick={() => handleSelectSection(section)}
              >
                {section.display}
              </Button>
            ))}
          </Stack>
        ) : null}

        {!fetchLoading && step === PICK_STEPS.AREA ? (
          <Stack gap={1}>
            {areas.map(area => (
              <Button
                key={area.guid}
                variant="contained"
                onClick={() => handleSelectArea(area)}
              >
                {area.display}
              </Button>
            ))}
            <Button onClick={() => setStep(PICK_STEPS.SECTION)}>بازگشت</Button>
          </Stack>
        ) : null}

        {!fetchLoading && step === PICK_STEPS.SHELF ? (
          <Stack gap={2} sx={{ pt: 2 }}>
            {direction === 'origin' ? (
              <TextField
                label="تعداد جابه جایی"
                type="number"
                value={count}
                onChange={event => setCount(event.target.value)}
                sx={{ maxWidth: 220 }}
              />
            ) : null}
            <Box sx={{ width: '100%', overflow: 'auto' }}>
              <ShelfAndPartMap
                loading={false}
                areaGuid={selectedArea?.guid}
                shelfsData={shelfMapData}
                isViewerModalOpen={isViewerModalOpen}
                onClickPart={handleClickPart}
                closeViewerModal={() => setIsViewerModalOpen(false)}
                partDisplay={selectedPart?.display}
                cells={cells}
                cellsLoading={cellsLoading || loading}
                onSelectCell={submitCell}
              />
            </Box>
            <Button onClick={() => setStep(PICK_STEPS.AREA)}>بازگشت</Button>
          </Stack>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default LocationPickerDialog;
